/**
 * @architecture-header 1
 * @layer module
 * @domain live
 * @responsibility Change the assistant mid-lesson through a few controls, scoped and time-boxed.
 * @state-owner local draft state; live policy store holds the applied overrides
 * @allowed-imports React; shared components and hooks; live policy model and store
 * @forbidden direct API client; free-text prompt editing
 * @public-api live run dashboard and intervention page
 * @side-effects writes an override with its scope, duration, author and reason
 * @tests modules/live/model/agent-policy.test.ts and the workspace Playwright flows
 */

import { useState } from 'react';
import { ModalDocument, SectionLabel } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { TEACHER_NAME } from '../../../shared/lib/demo-data';
import { currentTimeLabel } from '../../../shared/lib/selectors';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import {
  type AgentPolicy,
  FULL_SOLUTION_LABEL,
  HINT_DEPTH_LABEL,
  PLATFORM_GUARDRAILS,
  type PolicyScope,
  PROACTIVITY_LABEL,
  resolvePolicy,
  SCOPE_LABEL,
  SOURCES_LABEL,
  TURN_LENGTH_LABEL,
} from '../model/agent-policy';
import { selectOverrides, useAgentPolicy } from '../store/agent-policy';

const DURATIONS = [10, 20, 45];

/** One row per control, so the whole set stays scannable at six. */
function Choice<T extends string>({
  labels,
  onSelect,
  title,
  value,
}: {
  labels: Readonly<Record<T, string>>;
  onSelect: (next: T) => void;
  title: string;
  value: T;
}) {
  const t = useTranslator();
  return (
    <div className="apctl">
      <span>{t(title)}</span>
      <div className="fbar" style={{ margin: 0 }}>
        {(Object.keys(labels) as T[]).map((key) => (
          <button
            className={`ch ${value === key ? 'on' : ''}`}
            key={key}
            type="button"
            onClick={() => onSelect(key)}
          >
            {t(labels[key])}
          </button>
        ))}
      </div>
    </div>
  );
}

export function AgentPolicyDialog({ runId, student }: { runId: string; student?: string }) {
  const t = useTranslator();
  const { closeModal } = useOverlay();
  const notify = useWorkspaceUiStore((state) => state.notify);
  const overrides = useAgentPolicy((state) => state.overrides);
  const add = useAgentPolicy((state) => state.add);
  const revoke = useAgentPolicy((state) => state.revoke);

  const active = selectOverrides(overrides, runId);
  const { policy: current } = resolvePolicy(active, student);
  const [draft, setDraft] = useState<AgentPolicy>(current);
  const [scope, setScope] = useState<PolicyScope>(student ? 'student' : 'class');
  const [minutes, setMinutes] = useState(DURATIONS[0]);
  const [reason, setReason] = useState('');
  const patch = (next: Partial<AgentPolicy>) => setDraft((value) => ({ ...value, ...next }));

  const apply = () => {
    /** Only what actually changed is recorded, so the log reads as decisions not as snapshots. */
    const diff = Object.fromEntries(
      (Object.keys(draft) as (keyof AgentPolicy)[])
        .filter((key) => draft[key] !== current[key])
        .map((key) => [key, draft[key]]),
    ) as Partial<AgentPolicy>;
    if (!Object.keys(diff).length) {
      notify(t('沒有變更'));
      return;
    }
    add(runId, {
      id: `p${active.length + 1}-${currentTimeLabel()}`,
      scope,
      targets: scope === 'class' ? [] : student ? [student] : [],
      patch: diff,
      minutes,
      by: TEACHER_NAME,
      reason: reason.trim(),
      at: currentTimeLabel(),
    });
    closeModal();
    notify(`${t(SCOPE_LABEL[scope])} ${t('已套用新的助教設定')}`);
  };

  return (
    <ModalDocument
      className="apdoc"
      footer={
        <>
          <div className="sp" />
          <button className="bt" type="button" onClick={closeModal}>
            {t('取消')}
          </button>
          <button className="bt pri" type="button" onClick={apply}>
            {t('套用')}
          </button>
        </>
      }
      maxWidth={640}
      subtitle={student ? `${t('只改這位學生')}：${student}` : t('這節課的助教怎麼回應')}
      title={t('調整 AI 助教')}
    >
      <SectionLabel first>{t('這節課要怎麼幫')}</SectionLabel>
      <Choice
        labels={HINT_DEPTH_LABEL}
        onSelect={(hintDepth) => patch({ hintDepth })}
        title="提示深度"
        value={draft.hintDepth}
      />
      <div className="apctl">
        <span>{t('先要求學生嘗試')}</span>
        <div className="fbar" style={{ margin: 0 }}>
          <button
            className={`ch ${draft.attemptFirst ? 'on' : ''}`}
            type="button"
            onClick={() => patch({ attemptFirst: true })}
          >
            {t('要')}
          </button>
          <button
            className={`ch ${draft.attemptFirst ? '' : 'on'}`}
            type="button"
            onClick={() => patch({ attemptFirst: false })}
          >
            {t('不用')}
          </button>
        </div>
      </div>
      <Choice
        labels={FULL_SOLUTION_LABEL}
        onSelect={(fullSolution) => patch({ fullSolution })}
        title="完整解法"
        value={draft.fullSolution}
      />
      <Choice
        labels={TURN_LENGTH_LABEL}
        onSelect={(turnLength) => patch({ turnLength })}
        title="回合長度"
        value={draft.turnLength}
      />
      <Choice
        labels={PROACTIVITY_LABEL}
        onSelect={(proactivity) => patch({ proactivity })}
        title="主動程度"
        value={draft.proactivity}
      />
      <Choice
        labels={SOURCES_LABEL}
        onSelect={(sources) => patch({ sources })}
        title="允許來源"
        value={draft.sources}
      />

      <SectionLabel>{t('套用範圍與時間')}</SectionLabel>
      <div className="apctl">
        <span>{t('範圍')}</span>
        <div className="fbar" style={{ margin: 0 }}>
          {(Object.keys(SCOPE_LABEL) as PolicyScope[]).map((key) => (
            <button
              className={`ch ${scope === key ? 'on' : ''}`}
              disabled={key !== 'class' && !student}
              key={key}
              type="button"
              onClick={() => setScope(key)}
            >
              {t(SCOPE_LABEL[key])}
            </button>
          ))}
        </div>
      </div>
      <div className="apctl">
        <span>{t('維持多久')}</span>
        <div className="fbar" style={{ margin: 0 }}>
          {DURATIONS.map((value) => (
            <button
              className={`ch ${minutes === value ? 'on' : ''}`}
              key={value}
              type="button"
              onClick={() => setMinutes(value)}
            >
              {`${value} ${t('分鐘')}`}
            </button>
          ))}
        </div>
      </div>
      <label className="asfld">
        <span>{t('原因')}</span>
        <input
          onChange={(event) => setReason(event.target.value)}
          placeholder={t('例如：快下課了，允許講細一點')}
          value={reason}
        />
      </label>

      {active.length ? (
        <>
          <SectionLabel>{t('目前生效的調整')}</SectionLabel>
          {active.map((item) => (
            <div className="aprow" key={item.id}>
              <b>{t(SCOPE_LABEL[item.scope])}</b>
              <span>{`${item.at} · ${item.minutes} ${t('分鐘')} · ${item.by}`}</span>
              {item.reason ? <em>{item.reason}</em> : null}
              <div className="sp" />
              <button className="lk" type="button" onClick={() => revoke(runId, item.id)}>
                {t('收回')}
              </button>
            </div>
          ))}
        </>
      ) : null}

      <SectionLabel>{t('平台守則（不可關閉）')}</SectionLabel>
      <ul className="apfix">
        {PLATFORM_GUARDRAILS.map((rule) => (
          <li key={rule}>{t(rule)}</li>
        ))}
      </ul>
    </ModalDocument>
  );
}
